// Reusable workflow pipeline visualization component
import React, { useMemo, useEffect, useState, useCallback } from "react";
import { CheckCircle, XCircle, Clock, Circle } from "lucide-react";
import { ProgressBar } from "../Common/ProgressBar";
import { useAgentExecutions } from "../../hooks/useAgentExecutions";
import { usePipelineConfig } from "../../hooks/usePipelineConfig";
import { apiService } from "../../services/api";

// Helper to parse hierarchical step_id
interface ParsedStepId {
  isSubAgent: boolean;
  orchestratorStepId?: string;
  subAgentName?: string;
  processorStepId?: string;
  iteration?: number;
  fullStepId: string;
}

function parseStepId(stepId: string): ParsedStepId {
  const parts = stepId.split('.');
  
  // Sub-agent step IDs have format: orchestrator_ID.sub_agent_name.processor_ID.iteration
  if (parts.length >= 4) {
    return {
      isSubAgent: true,
      orchestratorStepId: parts[0],
      subAgentName: parts[1],
      processorStepId: parts[2],
      iteration: parseInt(parts[3], 10),
      fullStepId: stepId,
    };
  }
  
  // Main pipeline processor
  return {
    isSubAgent: false,
    fullStepId: stepId,
  };
}

interface WorkflowPipelineViewProps {
  workflowId: string;
  pipelineId?: number;
  refreshTrigger?: number;
  workflowStatus?: string;
}

export const WorkflowPipelineView: React.FC<WorkflowPipelineViewProps> = ({
  workflowId,
  pipelineId,
  refreshTrigger,
  workflowStatus,
}) => {
  const { config } = usePipelineConfig();
  const { agentExecutions, refetch: refetchAgentExecutions } =
    useAgentExecutions(workflowId);
  const [pipelineSteps, setPipelineSteps] = useState<any[] | null>(null);
  
  // Check if workflow is paused
  const isWorkflowPaused = workflowStatus === 'paused' || 
                           workflowStatus === 'waiting_approval' ||
                           workflowStatus === 'WAITING_APPROVAL';

  // Fetch database pipeline steps if workflow has pipeline_id
  useEffect(() => {
    if (pipelineId) {
      apiService
        .getPipelineSteps(pipelineId)
        .then((response) => {
          setPipelineSteps(response.steps);
        })
        .catch((error) => {
          console.error(
            `Error fetching pipeline steps for workflow ${workflowId}:`,
            error
          );
        });
    }
  }, [pipelineId, workflowId]);

  // Trigger agent executions refresh when refresh is triggered
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetchAgentExecutions();
    }
  }, [refreshTrigger, refetchAgentExecutions]);

  const getAgentStatus = useCallback((processorName: string, stepId?: string) => {
    if (!agentExecutions || !Array.isArray(agentExecutions)) {
      return "pending";
    }

    // Find execution matching both processor_type and step_id (if provided)
    const executions = agentExecutions
      .filter((exec) => {
        const processorMatch = exec.processor_type === processorName;
        const stepMatch = !stepId || exec.step_id === stepId;
        return processorMatch && stepMatch && exec.started_at;
      })
      .sort(
        (a, b) =>
          new Date(b.started_at!).getTime() -
          new Date(a.started_at!).getTime()
      );

    const execution = executions[0];
    if (!execution) {
      return "pending";
    }

    const statusLower = execution.status.toLowerCase();
    switch (statusLower) {
      case "completed":
        return "completed";
      case "in_progress":
      case "running":
      case "calling_sub_agent":
        return "in_progress";
      case "failed":
        return "failed";
      default:
        console.warn(`[WorkflowPipelineView] Unknown status for ${processorName}: ${execution.status}`);
        return "pending";
    }
  }, [agentExecutions]);

  // Create dynamic stages based on pipeline configuration
  const dynamicStages = useMemo(() => {
    // Use database pipeline steps if available
    if (pipelineSteps && pipelineSteps.length > 0) {
      const stages: Array<{
        name: string;
        status: string;
        isSubAgent?: boolean;
        indent?: number;
        iteration?: number;
        stepId?: string;
      }> = [
        { name: "Spec", status: "completed" },
      ];

      const sortedSteps = [...pipelineSteps].sort((a, b) => a.order - b.order);
      
      sortedSteps.forEach((step) => {
        const status = getAgentStatus(step.processor_name, step.step_id);
        
        // Add main pipeline step
        stages.push({
          name: step.name,
          status,
          stepId: step.step_id,
        });

        // Recursive helper to process sub-agents at any depth
        const processSubAgents = (orchestratorStepId: string, subAgents: any[], indentLevel: number) => {
          subAgents.forEach((subAgent: any) => {
            const displayName = subAgent.display_name || subAgent.name;
            
            // Find all executions for this sub-agent
            const subAgentExecutions = agentExecutions?.filter((exec) => {
              if (!exec.step_id) return false;
              const parsed = parseStepId(exec.step_id);
              return parsed.isSubAgent && 
                     parsed.orchestratorStepId === orchestratorStepId &&
                     parsed.subAgentName === subAgent.name;
            }) || [];
            
            if (subAgentExecutions.length > 0) {
              // Sort executions by iteration number
              const sortedExecutions = [...subAgentExecutions].sort((a, b) => {
                const aIteration = a.step_id ? parseStepId(a.step_id).iteration || 0 : 0;
                const bIteration = b.step_id ? parseStepId(b.step_id).iteration || 0 : 0;
                return aIteration - bIteration;
              });

              // Add each execution iteration
              sortedExecutions.forEach((exec) => {
                const parsed = parseStepId(exec.step_id!);
                const iterationLabel = parsed.iteration ? ` (.${parsed.iteration})` : '';
                
                stages.push({
                  name: `${displayName}${iterationLabel}`,
                  status: exec.status,
                  isSubAgent: true,
                  indent: indentLevel,
                  iteration: parsed.iteration,
                  stepId: exec.step_id,
                });
              });
            } else {
              // No executions yet - show as pending
              stages.push({
                name: displayName,
                status: 'pending',
                isSubAgent: true,
                indent: indentLevel,
              });
            }

            // RECURSIVE: Check if this sub-agent has steps that are orchestrators
            if (subAgent.steps && subAgent.steps.length > 0) {
              subAgent.steps.forEach((subStep: any) => {
                if (subStep.processor_name === 'orchestrator' && subStep.config?.sub_agents) {
                  // This sub-agent step is itself an orchestrator - recursively process its sub-agents
                  processSubAgents(subStep.step_id, subStep.config.sub_agents, indentLevel + 1);
                }
              });
            }
          });
        };

        // If this is an orchestrator, show configured sub-agents from pipeline definition
        if (step.processor_name === 'orchestrator' && step.config?.sub_agents) {
          processSubAgents(step.step_id, step.config.sub_agents, 1);
        }
      });

      return stages;
    }

    // Fallback to YAML config
    if (!config?.execution_order || !config?.config?.agents) {
      return [
        { name: "Spec", status: "completed" },
        { name: "Pseudocode", status: getAgentStatus("pseudocode") },
        { name: "Tasks", status: getAgentStatus("ticket") },
        { name: "Workspace", status: getAgentStatus("workspace") },
        { name: "Coding", status: getAgentStatus("coding") },
      ];
    }

    const stages = [
      { name: "Spec", status: "completed" },
    ];

    config.execution_order.forEach((agentId) => {
      const agent = config.config.agents.find((a) => a.id === agentId);
      if (agent) {
        const displayName = agent.name
          ?.replace(" Agent", "")
          .replace(" Generation", "")
          .replace(" Breakdown", "")
          .replace(" Creation", "") || agentId;

        stages.push({
          name: displayName,
          status: getAgentStatus(agentId),
        });
      }
    });

    return stages;
  }, [config, agentExecutions, pipelineSteps, getAgentStatus]);

  // Calculate progress percentage
  const progress = useMemo(() => {
    const completedStages = dynamicStages.filter(
      (stage) => stage.status === "completed"
    ).length;
    return Math.round((completedStages / dynamicStages.length) * 100);
  }, [dynamicStages]);

  const getStageIcon = (status: string, isPaused: boolean = false) => {
    const statusLower = (status || '').toLowerCase();
    
    switch (statusLower) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "in_progress":
      case "running":
      case "calling_sub_agent":
        // Show amber (no pulse) if paused, blue (with pulse) if actively running
        return isPaused ? (
          <Clock className="h-4 w-4 text-orange-500" />
        ) : (
          <Clock className="h-4 w-4 text-blue-500 animate-pulse" />
        );
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Circle className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getStageColor = (status: string, isPaused: boolean = false) => {
    const statusLower = (status || '').toLowerCase();
    
    switch (statusLower) {
      case "completed":
        return "text-green-600 dark:text-green-400";
      case "in_progress":
      case "running":
      case "calling_sub_agent":
        // Show amber if paused, blue if actively running
        return isPaused 
          ? "text-orange-600 dark:text-orange-400"
          : "text-blue-600 dark:text-blue-400";
      case "failed":
        return "text-red-600 dark:text-red-400";
      default:
        return "text-gray-500 dark:text-gray-400";
    }
  };

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {progress}%
          </span>
        </div>
        <ProgressBar progress={progress} />
      </div>

      {/* Pipeline Stages */}
      <div className="space-y-2">
        {dynamicStages.map((stage, index) => {
          const isLastSubAgent = 
            stage.isSubAgent && 
            (index === dynamicStages.length - 1 || !dynamicStages[index + 1].isSubAgent);
          
          // Check if this specific stage is paused (running but workflow is paused)
          const statusLower = (stage.status || '').toLowerCase();
          const isStageRunning = statusLower === 'in_progress' || 
                                 statusLower === 'running' || 
                                 statusLower === 'calling_sub_agent';
          const isStagePaused = isWorkflowPaused && isStageRunning;
          
          return (
            <div 
              key={index} 
              className="flex items-center space-x-3"
              style={{ 
                paddingLeft: stage.indent ? `${stage.indent * 24}px` : '0px' 
              }}
            >
              {stage.isSubAgent && (
                <span className="text-gray-400 dark:text-gray-600 text-sm mr-1">
                  {isLastSubAgent ? '└─' : '├─'}
                </span>
              )}
              {getStageIcon(stage.status, isStagePaused)}
              <span
                className={`text-sm font-medium ${getStageColor(
                  stage.status,
                  isStagePaused
                )} ${stage.isSubAgent ? 'text-gray-700 dark:text-gray-300' : ''}`}
              >
                {stage.name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

